import React from 'react';

import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {Delete as DeleteIcon} from '@mui/icons-material';
import {useTheme} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {Types, VehiclePaginationProps, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {AsvIcon, DroneIcon, HoverbikeIcon} from '@wheeebo/polaris-svg';
import {
  ConfirmationDialog,
  CircleLoader,
  Head,
  LoadError,
  SquareChip,
  TooltipButton,
  useErrorHandler,
} from '@wheeebo/polaris-ui';

const INITIAL_ITEM_COUNT = 25;
const ACTIVE_VEHICLE_TIME_THRESHOLD = 10000; // 10 seconds
const VEHICLE_UPDATE_INTERVAEL_MSEC = 10000; // 10 seconds

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.member.DronListPage.header.title',
    defaultMessage: 'Vehicle List',
  },
  headerSubTitle: {
    id: 'pages.member.DronListPage.header.subTitle',
    defaultMessage:
      'This page lists vehicles owned by groups that you belong to. Each vehicle can belong to multiple groups.',
  },
  cellVehicleName: {
    id: 'pages.member.DronListPage.cell.vehicleName',
    defaultMessage: 'Vehicle name',
  },
  cellVehicleType: {
    id: 'pages.member.DronListPage.cell.vehicleType',
    defaultMessage: 'Vehicle Type',
  },
  cellGroupName: {
    id: 'pages.member.DronListPage.cell.groupName',
    defaultMessage: 'Owner Groups',
  },
  cellActivationStatus: {
    id: 'pages.member.DronListPage.cell.activationStatus',
    defaultMessage: 'Activation Status',
  },
  errorNoVehicle: {
    id: 'pages.member.GroupListPage.error.noVehicle',
    defaultMessage: 'vehicle not found',
  },
  dialogConfirmationTitle: {
    id: 'pages.member.GroupListPage.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeleteConfirmationContent: {
    id: 'pages.member.GroupListPage.dialog.deleteConfirmationContent',
    defaultMessage: 'Are you really going to delete this group?',
  },
  successVehicleDeleted: {
    id: 'pages.member.GroupListPage.success.vehicleDeleted',
    defaultMessage: 'Vehicle deleted',
  },
});

interface RowProps {
  vehicle: Types.Vehicle;
  onDelete: (vehicleId: Types.UUID) => void;
}

function Row({vehicle, onDelete}: RowProps) {
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();
  const {account} = useAuth();
  const active = Date.now() - vehicle.lastHeartbeatAt < ACTIVE_VEHICLE_TIME_THRESHOLD;
  return (
    <TableRow hover sx={{cursor: 'pointer'}} onClick={() => navigate(`/vehicles/${vehicle.id}`)}>
      <TableCell sx={{pl: 1 / 4}}>
        <Stack direction="row" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Avatar variant="rounded" src={vehicle.imageUrl} sx={{width: 32, height: 32, backgroundColor: 'transparent'}}>
            <SvgIcon color="disabled">
              {vehicle.vehicleType === Types.VehicleTypeHoverbike ? (
                <HoverbikeIcon />
              ) : vehicle.vehicleType === Types.VehicleTypeAsv ? (
                <AsvIcon />
              ) : (
                <DroneIcon />
              )}
            </SvgIcon>
          </Avatar>
          <Typography variant="body2">{vehicle.name}</Typography>
          {isBigScreen && vehicle.owner?.id === account?.id ? <SquareChip label="Yours" color="primary" /> : null}
          {active ? <SquareChip label="Active" color="success" /> : null}
        </Stack>
      </TableCell>
      <TableCell>
        <SquareChip label={vehicle.vehicleType} />
      </TableCell>
      <TableCell>
        <Stack direction="row" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {vehicle.groups?.map((e) => (
            <Typography key={e.id} variant="body2" sx={{textDecoration: 'underline'}}>
              {e.group.name}
            </Typography>
          ))}
        </Stack>
      </TableCell>
      {isBigScreen ? (
        <TableCell>
          <SquareChip
            label={vehicle.activationConfirmed ? 'Activated' : 'Not-activated'}
            color={vehicle.activationConfirmed ? 'primary' : 'error'}
          />
        </TableCell>
      ) : null}
      {isBigScreen ? (
        <TableCell align="right">
          {vehicle.owner?.id === account?.id ? (
            <TooltipButton
              message="Delete"
              icon={<DeleteIcon fontSize="small" />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onDelete(vehicle.id);
              }}
            />
          ) : null}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

const MemoizedRow = React.memo(Row, (prev, next) => {
  const now = Date.now();
  const prevActive = now - ACTIVE_VEHICLE_TIME_THRESHOLD - prev.vehicle.lastHeartbeatAt < ACTIVE_VEHICLE_TIME_THRESHOLD;
  const nextActive = now - next.vehicle.lastHeartbeatAt < ACTIVE_VEHICLE_TIME_THRESHOLD;
  // Rerender the component only when the active status is changed.
  return prevActive != nextActive;
});

function DronListPage(): JSX.Element {
  const intl = useIntl();
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('sm'));
  const {csrfToken} = useCsrfToken();
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(0);
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<Types.UUID>();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const options: VehiclePaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    preload: ['groups', 'owner'],
  };
  const {data, isError, isLoading} = useQuery(API.Vehicles.listKey(options), () => API.Vehicles.list(options), {
    refetchInterval: VEHICLE_UPDATE_INTERVAEL_MSEC,
  });
  const handleDelete = (vehicleId: Types.UUID) => {
    setSelectedVehicleId(vehicleId);
    setDeleteDialogOpen(true);
  };
  const {mutate: deleteVehicle} = useMutation<unknown, unknown>(
    () => {
      setDeleteDialogOpen(false);
      if (!selectedVehicleId) {
        showError(new Error(intl.formatMessage(msgs.errorNoVehicle)));
        return Promise.reject({});
      }
      return API.Vehicles.delete_(csrfToken, selectedVehicleId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        showSuccess(intl.formatMessage(msgs.successVehicleDeleted));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  return (
    <Grid container spacing={1}>
      <Head title="Vehicles" />
      <Grid item xs={12} sm={8}>
        <Card>
          <CardHeader
            title={intl.formatMessage(msgs.headerTitle)}
            subheader={intl.formatMessage(msgs.headerSubTitle)}
          />
          <CardContent>
            {isLoading ? <CircleLoader /> : null}
            {isError ? <LoadError /> : null}
            {data && data.total > 0 ? (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{intl.formatMessage(msgs.cellVehicleName)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellVehicleType)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellGroupName)}</TableCell>
                      {isBigScreen ? <TableCell>{intl.formatMessage(msgs.cellActivationStatus)}</TableCell> : null}
                      {isBigScreen ? <TableCell /> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.items?.map((e: Types.Vehicle) => (
                      <MemoizedRow key={e.id} vehicle={e} onDelete={handleDelete} />
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                        count={data?.total || 0}
                        rowsPerPage={INITIAL_ITEM_COUNT}
                        page={page}
                        SelectProps={{
                          inputProps: {'aria-label': 'rows per page'},
                        }}
                        onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                          setPage(newPage)
                        }
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2">No record found.</Typography>
            )}
          </CardContent>
        </Card>
        <ConfirmationDialog
          open={deleteDialogOpen}
          title={intl.formatMessage(msgs.dialogConfirmationTitle)}
          description={intl.formatMessage(msgs.dialogDeleteConfirmationContent)}
          onCancel={() => setDeleteDialogOpen(false)}
          onConfirm={() => deleteVehicle()}
        />
      </Grid>
    </Grid>
  );
}

export default React.memo(DronListPage);
